import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { StopCircle, Radio } from 'lucide-react';
import { toast } from 'sonner';
import InjectQuickViewModal from '../InjectQuickViewModal';
import ObjectiveQuickViewModal from '../ObjectiveQuickViewModal';

/* ═══════════════════════════════════════════════════════════════════════
   Inject Create / Edit Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function InjectFormDialog({
  open, editingInjectId, injectForm, setInjectForm,
  onSubmitCreate, onSubmitUpdate, onClose, resetInjectForm,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-secondary">{editingInjectId ? 'Edit Inject' : 'Create Inject'}</DialogTitle>
          <DialogDescription>{editingInjectId ? 'Update inject details' : 'Add a new inject to this exercise'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={editingInjectId ? onSubmitUpdate : onSubmitCreate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="inject-id">Inject ID (Number)</Label>
              <Input id="inject-id" value={injectForm.inject_number || ''} onChange={(e) => setInjectForm({...injectForm, inject_number: e.target.value})} placeholder="e.g. 1, 2, 3..." data-testid="inject-id-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inject-title">Title *</Label>
              <Input id="inject-title" value={injectForm.title} onChange={(e) => setInjectForm({...injectForm, title: e.target.value})} required data-testid="inject-title-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="inject-content">Inject Information *</Label>
            <Textarea id="inject-content" value={injectForm.content} onChange={(e) => setInjectForm({...injectForm, content: e.target.value})} rows={4} required data-testid="inject-content-input" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="inject-time">Inject Time (UTC)</Label>
              <Input id="inject-time" value={injectForm.inject_time} onChange={(e) => setInjectForm({ ...injectForm, inject_time: e.target.value })} placeholder="Oct 17th @ 10:00" data-testid="inject-time-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inject-method">Method</Label>
              <Input id="inject-method" value={injectForm.method} onChange={(e) => setInjectForm({ ...injectForm, method: e.target.value })} data-testid="inject-method-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inject-dependancy">Dependancy</Label>
              <Input id="inject-dependancy" value={injectForm.dependancy} onChange={(e) => setInjectForm({ ...injectForm, dependancy: e.target.value })} data-testid="inject-dependancy-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inject-recipient">Recipient</Label>
              <Input id="inject-recipient" value={injectForm.recipient} onChange={(e) => setInjectForm({ ...injectForm, recipient: e.target.value })} data-testid="inject-recipient-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inject-specific-function">Specific Function</Label>
              <Input id="inject-specific-function" value={injectForm.specific_function} onChange={(e) => setInjectForm({ ...injectForm, specific_function: e.target.value })} data-testid="inject-specific-function-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inject-reference">Reference (Objective ID)</Label>
              <Input id="inject-reference" value={injectForm.reference} onChange={(e) => setInjectForm({ ...injectForm, reference: e.target.value })} data-testid="inject-reference-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inject-injected-by">Injected By</Label>
              <Input id="inject-injected-by" value={injectForm.injected_by} onChange={(e) => setInjectForm({ ...injectForm, injected_by: e.target.value })} data-testid="inject-injected-by-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="inject-objective">Objective</Label>
            <Textarea id="inject-objective" value={injectForm.objective} onChange={(e) => setInjectForm({ ...injectForm, objective: e.target.value })} rows={3} data-testid="inject-objective-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inject-supplemental">Supplemental Information</Label>
            <Textarea id="inject-supplemental" value={injectForm.supplemental_information} onChange={(e) => setInjectForm({ ...injectForm, supplemental_information: e.target.value })} rows={3} data-testid="inject-supplemental-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inject-key-outcomes">Key Outcomes</Label>
            <Textarea id="inject-key-outcomes" value={injectForm.key_outcomes} onChange={(e) => setInjectForm({ ...injectForm, key_outcomes: e.target.value })} rows={3} data-testid="inject-key-outcomes-input" />
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select value={injectForm.priority} onValueChange={(v) => setInjectForm({...injectForm, priority: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="submit-inject-btn">{editingInjectId ? 'Save Changes' : 'Create Inject'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Objective Create / Edit Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function ObjectiveCreateDialog({ open, onOpenChange, newObjective, setNewObjective, creatingObjective, onSubmit, editingObjectiveId }) {
  const isEdit = Boolean(editingObjectiveId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="exercise-create-objective-modal">
        <DialogHeader>
          <DialogTitle className="font-secondary">{isEdit ? 'Edit Objective' : 'Create Objective'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="exercise-objective-code">Objective Code *</Label>
            <Input id="exercise-objective-code" value={newObjective.objective_code} onChange={(e) => setNewObjective({ ...newObjective, objective_code: e.target.value })} placeholder="e.g., OBJ-001" required data-testid="exercise-objective-code-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="exercise-objective-text">Objective Text</Label>
            <Textarea id="exercise-objective-text" value={newObjective.objective_text} onChange={(e) => setNewObjective({ ...newObjective, objective_text: e.target.value })} placeholder="Describe the objective..." rows={3} data-testid="exercise-objective-text-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="exercise-objective-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={creatingObjective} data-testid="exercise-objective-submit-btn">
              {creatingObjective ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Objective')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Inject Notes Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function InjectNotesDialog({
  open, onOpenChange, selectedInject, responses,
  responseText, setResponseText, attachments, setAttachments,
  onSubmit, getFileUrl, readOnly = false,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" onInteractOutside={(e) => e.preventDefault()} data-testid="inject-notes-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="font-secondary">Notes</DialogTitle>
          {selectedInject && <DialogDescription>Thread for: {selectedInject.title}</DialogDescription>}
        </DialogHeader>
        {/* Scrollable thread area (flex-1 takes remaining space; form below is sticky) */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3 space-y-3">
          {selectedInject && (
            <div className="p-3 bg-muted rounded-lg"><p className="text-sm">{selectedInject.content}</p></div>
          )}
          <div className="space-y-3">
            {responses.filter((r) => r.inject_id === selectedInject?.inject_id).map((r) => (
              <div key={r.response_id} className="p-3 rounded-lg border">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">{r.sender_role === 'participant' ? `Participant: ${r.user_name}` : `${r.sender_role}: ${r.user_name}`}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.submitted_at).toLocaleString()}</div>
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap">{r.response_text}</p>
                {!!(r.attachments || []).length && (
                  <div className="mt-2 space-y-1">
                    {(r.attachments_meta?.length ? r.attachments_meta : (r.attachments || []).map((fid) => ({ file_id: fid }))).map((att) => {
                      const fileId = att.file_id || att;
                      const filename = att.filename || fileId;
                      const isImage = (att.content_type || '').startsWith('image/');
                      const fileUrl = getFileUrl(fileId);
                      return (
                        <div key={fileId} className="flex items-center gap-2">
                          <a className="text-xs underline text-primary" href={fileUrl} target="_blank" rel="noreferrer" data-testid="attachment-download-link">{filename}</a>
                          {isImage && <img src={fileUrl} alt={filename} className="h-12 w-12 rounded-md object-cover border" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {responses.filter((r) => r.inject_id === selectedInject?.inject_id).length === 0 && (
              <div className="text-sm text-muted-foreground">No notes yet</div>
            )}
          </div>
        </div>
        {/* Sticky input form */}
        {readOnly ? (
          <div className="border-t bg-card px-6 py-3 shrink-0">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={onSubmit} className="border-t bg-card px-6 py-4 space-y-3 shrink-0">
          <div className="space-y-2">
            <Label htmlFor="response-text">Message *</Label>
            <Textarea id="response-text" value={responseText} onChange={(e) => setResponseText(e.target.value)} rows={3} required placeholder="Type a note..." data-testid="response-text-input" />
          </div>
          <div className="space-y-2">
            <Label>Attachments (optional)</Label>
            <Input type="file" multiple onChange={(e) => setAttachments(Array.from(e.target.files || []))} />
            <p className="text-xs text-muted-foreground">Allowed: images/pdf/docx (max ~25MB each)</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button type="submit" data-testid="submit-response-btn">Send</Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Objective Notes Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function ObjectiveNotesDialog({
  open, onOpenChange, selectedObjective, responses,
  responseText, setResponseText, attachments, setAttachments,
  onSubmitNote, getFileUrl, readOnly = false,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" onInteractOutside={(e) => e.preventDefault()} data-testid="objective-notes-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="font-secondary">Objective Notes</DialogTitle>
          {selectedObjective && <DialogDescription>Thread for Objective: {selectedObjective.objective_code}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3 space-y-3">
          {selectedObjective && (
            <div className="p-3 bg-muted rounded-lg"><p className="text-sm">{selectedObjective.objective_text}</p></div>
          )}
          <div className="space-y-3">
            {responses.filter((r) => r.objective_id === selectedObjective?.objective_id).map((r) => (
              <div key={r.response_id} className="p-3 rounded-lg border">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">{r.sender_role === 'participant' ? `Participant: ${r.user_name}` : `${r.sender_role}: ${r.user_name}`}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.submitted_at).toLocaleString()}</div>
                </div>
                <p className="text-sm mt-2 whitespace-pre-wrap">{r.response_text}</p>
                {!!(r.attachments || []).length && (
                  <div className="mt-2 space-y-1">
                    {(r.attachments_meta?.length ? r.attachments_meta : (r.attachments || []).map((fid) => ({ file_id: fid }))).map((att) => {
                      const fileId = att.file_id || att;
                      const filename = att.filename || fileId;
                      const isImage = (att.content_type || '').startsWith('image/');
                      const fileUrl = getFileUrl(fileId);
                      return (
                        <div key={fileId} className="flex items-center gap-2">
                          <a className="text-xs underline text-primary" href={fileUrl} target="_blank" rel="noreferrer" data-testid="objective-attachment-download-link">{filename}</a>
                          {isImage && <img src={fileUrl} alt={filename} className="h-12 w-12 rounded-md object-cover border" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {responses.filter((r) => r.objective_id === selectedObjective?.objective_id).length === 0 && (
              <div className="text-sm text-muted-foreground">No notes yet for this objective</div>
            )}
          </div>
        </div>
        {readOnly ? (
          <div className="border-t bg-card px-6 py-3 shrink-0">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={onSubmitNote} className="border-t bg-card px-6 py-4 space-y-3 shrink-0">
          <div className="space-y-2">
            <Label htmlFor="objective-response-text">Message *</Label>
            <Textarea id="objective-response-text" value={responseText} onChange={(e) => setResponseText(e.target.value)} rows={3} required placeholder="Type a note..." />
          </div>
          <div className="space-y-2">
            <Label>Attachments (optional)</Label>
            <Input type="file" multiple onChange={(e) => setAttachments(Array.from(e.target.files || []))} />
            <p className="text-xs text-muted-foreground">Allowed: images/pdf/docx (max ~25MB each)</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button type="submit">Send</Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Assign Users Dialogs (Participants / Controllers / Observers)
   ═══════════════════════════════════════════════════════════════════════ */
function AssignUsersDialog({ open, onOpenChange, title, description, items, selectedIds, toggleId, onSave, canSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-secondary">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users available</p>
          ) : (
            items.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 p-2 rounded hover:bg-accent">
                <Checkbox checked={selectedIds.includes(u.user_id)} onCheckedChange={() => toggleId(u.user_id)} id={`assign-${u.user_id}`} />
                <label htmlFor={`assign-${u.user_id}`} className="text-sm cursor-pointer flex-1">
                  {u.name} <span className="text-muted-foreground">({u.email})</span>
                </label>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={!canSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   End Exercise Confirmation Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function EndExerciseDialog({ open, onOpenChange, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-secondary text-destructive">End Exercise?</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-muted-foreground">Are you sure you want to end this exercise? This will mark the exercise as completed.</p>
          <p className="text-sm text-muted-foreground mt-2">You can reactivate the exercise later if needed.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm}><StopCircle className="w-4 h-4 mr-2" />Yes, End Exercise</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Status Change Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function StatusChangeDialog({ open, onOpenChange, exercise, exerciseId, exercisesApi, loadExercise, getStatusBadgeClass }) {
  const changeStatus = async (action, label) => {
    try {
      await exercisesApi.control(exerciseId, action);
      toast.success(`Exercise ${label}`);
      loadExercise();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Failed to change status');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-secondary">Change Exercise Status</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Current status: <Badge className={`${getStatusBadgeClass(exercise?.status)} border capitalize ml-2`}>{exercise?.status}</Badge>
          </p>
          <p className="text-sm">Select a new status for this exercise:</p>
          <div className="grid grid-cols-3 gap-2">
            <Button variant={exercise?.status === 'draft' ? 'default' : 'outline'} onClick={() => changeStatus('draft', 'set to draft')} disabled={exercise?.status === 'draft'}>Draft</Button>
            <Button variant={exercise?.status === 'active' ? 'default' : 'outline'} onClick={() => changeStatus('start', 'activated')} disabled={exercise?.status === 'active'}>Active</Button>
            <Button variant={exercise?.status === 'completed' ? 'default' : 'outline'} onClick={() => changeStatus('end', 'completed')} disabled={exercise?.status === 'completed'}>Completed</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MP Publish Confirmation Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function MPPublishDialog({
  open, onOpenChange, mpPublishInjectId, mpPublishPlatform, setMpPublishPlatform,
  mpBridgeStatus, onPublish, onClose,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-secondary">Publish to Media Portal?</DialogTitle>
          <DialogDescription>This will create a media post from this inject and publish it to the linked Media Portal exercise.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <p className="text-sm text-muted-foreground">The inject content will be used as the post content. You can edit the post in the Media Portal after publishing.</p>
          {mpBridgeStatus?.selected_platforms?.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Select Platform</label>
              <Select value={mpPublishPlatform} onValueChange={setMpPublishPlatform}>
                <SelectTrigger className="w-full" data-testid="mp-platform-select"><SelectValue placeholder="Choose a platform..." /></SelectTrigger>
                <SelectContent>
                  {mpBridgeStatus.selected_platforms.map(p => (
                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1).replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={async () => {
              onOpenChange(false);
              if (mpPublishInjectId) {
                await onPublish(mpPublishInjectId, mpPublishPlatform || undefined);
              }
              onClose();
            }}
            disabled={mpBridgeStatus?.selected_platforms?.length > 0 && !mpPublishPlatform}
            data-testid="confirm-mp-publish-btn"
          >
            <Radio className="w-4 h-4 mr-1" />Publish to Media Portal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   CSV Import Dialog
   ═══════════════════════════════════════════════════════════════════════ */
function CsvImportDialog({ open, onOpenChange, csvImporting, csvFileRef, onImport }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import Injects from CSV/Excel</DialogTitle>
          <DialogDescription>Upload a CSV or Excel file with inject data. Columns: title, content, priority, recipient_role, scheduled_time, category.</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <input
            ref={csvFileRef}
            type="file"
            accept=".csv,.xlsx,.xlsm,.xls"
            onChange={onImport}
            className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-input file:text-sm file:font-medium file:bg-background hover:file:bg-muted cursor-pointer"
            disabled={csvImporting}
            data-testid="csv-import-input"
          />
          {csvImporting && <p className="text-xs text-muted-foreground">Importing...</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={csvImporting}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ExerciseDialogs — Container for all exercise detail dialogs
   ═══════════════════════════════════════════════════════════════════════ */
export function ExerciseDialogs({
  // Inject form
  injectDialogOpen, setInjectDialogOpen, editingInjectId, setEditingInjectId,
  injectForm, setInjectForm, resetInjectForm,
  handleCreateInject, handleUpdateInject,
  // Objective create / edit
  objectiveCreateOpen, setObjectiveCreateOpen,
  newObjective, setNewObjective, creatingObjective, handleCreateObjective,
  editingObjectiveId,
  openEditObjective,
  // Quick-view modals
  objectiveViewOpen, setObjectiveViewOpen, viewObjective,
  setSelectedObjective, setObjectiveNotesOpen,
  injectViewOpen, setInjectViewOpen, viewInject,
  openResponseDialog, openEditInject, isController, isObserver,
  // Inject notes
  responseDialogOpen, setResponseDialogOpen, selectedInject,
  responses, responseText, setResponseText,
  attachments, setAttachments, handleSubmitResponse, getFileUrl,
  // Objective notes
  objectiveNotesOpen, selectedObjective,
  handleSubmitObjectiveNote,
  // Assign users
  assignDialogOpen, setAssignDialogOpen,
  participants, selectedParticipantIds, toggleParticipant, handleSaveParticipants,
  assignControllersOpen, setAssignControllersOpen,
  controllers, selectedControllerIds, toggleController, handleSaveControllers,
  assignObserversOpen, setAssignObserversOpen,
  observers, selectedObserverIds, toggleObserver, handleSaveObservers,
  isAdmin, canManageExercise,
  // Exercise control
  endConfirmOpen, setEndConfirmOpen, handleControlAction,
  statusChangeOpen, setStatusChangeOpen,
  exercise, exerciseId, exercisesApi, loadExercise, getStatusBadgeClass,
  // MP publish
  mpPublishConfirmOpen, setMpPublishConfirmOpen,
  mpPublishInjectId, setMpPublishInjectId,
  mpPublishPlatform, setMpPublishPlatform,
  mpBridgeStatus, handlePublishToMediaPortal,
  // CSV import
  csvImportOpen, setCsvImportOpen, csvImporting, csvFileRef, handleCsvImport,
  // Inject DB picker
  injectDbPickerOpen, setInjectDbPickerOpen, loadInjects,
}) {
  return (
    <>
      {/* Inject Create/Edit */}
      <InjectFormDialog
        open={injectDialogOpen}
        editingInjectId={editingInjectId}
        injectForm={injectForm}
        setInjectForm={setInjectForm}
        onSubmitCreate={handleCreateInject}
        onSubmitUpdate={handleUpdateInject}
        onClose={() => { setInjectDialogOpen(false); setEditingInjectId(null); resetInjectForm(); }}
        resetInjectForm={resetInjectForm}
      />

      {/* Objective Quick View */}
      <ObjectiveQuickViewModal
        open={objectiveViewOpen}
        onOpenChange={setObjectiveViewOpen}
        objective={viewObjective}
        onOpenNotes={() => {
          if (viewObjective) { setSelectedObjective(viewObjective); setObjectiveNotesOpen(true); }
          setObjectiveViewOpen(false);
        }}
        onEdit={isController && isController() && openEditObjective ? (obj) => {
          setObjectiveViewOpen(false);
          openEditObjective(obj);
        } : null}
      />

      {/* Objective Create / Edit */}
      <ObjectiveCreateDialog
        open={objectiveCreateOpen}
        onOpenChange={setObjectiveCreateOpen}
        newObjective={newObjective}
        setNewObjective={setNewObjective}
        creatingObjective={creatingObjective}
        onSubmit={handleCreateObjective}
        editingObjectiveId={editingObjectiveId}
      />

      {/* Inject Quick View */}
      <InjectQuickViewModal
        open={injectViewOpen}
        onOpenChange={setInjectViewOpen}
        inject={viewInject}
        onOpenNotes={() => {
          if (viewInject) openResponseDialog(viewInject);
          setInjectViewOpen(false);
        }}
        onEdit={isController() ? openEditInject : null}
      />

      {/* Inject Notes */}
      <InjectNotesDialog
        open={responseDialogOpen}
        onOpenChange={setResponseDialogOpen}
        selectedInject={selectedInject}
        responses={responses}
        responseText={responseText}
        setResponseText={setResponseText}
        attachments={attachments}
        setAttachments={setAttachments}
        onSubmit={handleSubmitResponse}
        getFileUrl={getFileUrl}
        readOnly={isObserver ? isObserver() : false}
      />

      {/* Objective Notes */}
      <ObjectiveNotesDialog
        open={objectiveNotesOpen}
        onOpenChange={setObjectiveNotesOpen}
        selectedObjective={selectedObjective}
        responses={responses}
        responseText={responseText}
        setResponseText={setResponseText}
        attachments={attachments}
        setAttachments={setAttachments}
        onSubmitNote={handleSubmitObjectiveNote}
        getFileUrl={getFileUrl}
        readOnly={isObserver ? isObserver() : false}
      />

      {/* Assign Participants */}
      <AssignUsersDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        title="Assign Participants"
        description="Select the participants for this exercise"
        items={participants}
        selectedIds={selectedParticipantIds}
        toggleId={toggleParticipant}
        onSave={handleSaveParticipants}
        canSave={canManageExercise || isAdmin}
      />

      {/* Assign Controllers */}
      <AssignUsersDialog
        open={assignControllersOpen}
        onOpenChange={setAssignControllersOpen}
        title="Assign Controllers (Simcell)"
        description="Select the controllers / simcell users for this exercise"
        items={controllers}
        selectedIds={selectedControllerIds}
        toggleId={toggleController}
        onSave={handleSaveControllers}
        canSave={isAdmin}
      />

      {/* Assign Observers */}
      <AssignUsersDialog
        open={assignObserversOpen}
        onOpenChange={setAssignObserversOpen}
        title="Assign Observers"
        description="Select the observers for this exercise"
        items={observers}
        selectedIds={selectedObserverIds}
        toggleId={toggleObserver}
        onSave={handleSaveObservers}
        canSave={isAdmin}
      />

      {/* End Exercise Confirmation */}
      <EndExerciseDialog
        open={endConfirmOpen}
        onOpenChange={setEndConfirmOpen}
        onConfirm={() => { handleControlAction('end'); setEndConfirmOpen(false); }}
      />

      {/* Status Change */}
      <StatusChangeDialog
        open={statusChangeOpen}
        onOpenChange={setStatusChangeOpen}
        exercise={exercise}
        exerciseId={exerciseId}
        exercisesApi={exercisesApi}
        loadExercise={loadExercise}
        getStatusBadgeClass={getStatusBadgeClass}
      />

      {/* MP Publish Confirmation */}
      <MPPublishDialog
        open={mpPublishConfirmOpen}
        onOpenChange={setMpPublishConfirmOpen}
        mpPublishInjectId={mpPublishInjectId}
        mpPublishPlatform={mpPublishPlatform}
        setMpPublishPlatform={setMpPublishPlatform}
        mpBridgeStatus={mpBridgeStatus}
        onPublish={handlePublishToMediaPortal}
        onClose={() => { setMpPublishInjectId(null); setMpPublishPlatform(''); }}
      />

      {/* CSV Import */}
      <CsvImportDialog
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        csvImporting={csvImporting}
        csvFileRef={csvFileRef}
        onImport={handleCsvImport}
      />
    </>
  );
}
